import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Chip,
} from "@mui/material";
import { useRef, useState } from "react";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";
import { ProgressDialog } from "../plungerPage/progressPopup";
import { Refresh, Download } from "@mui/icons-material";

const uint16ToInt16 = (n) => (n > 0x7fff ? n - 0x10000 : n);

const FAULT_MAPS = {
  41805: [
    "SPN2978_InputFault",
    "FanSpeedPinFault",
    "NoFanSpeed",
    "ExcessFanSpeed",
    "FanSpeedError",
    "OutputHardwareFault",
    "APT_Fault",
    "InputSpeedError",
    "CAN_BUS_OFF",
    "CAN_DriverError",
    "CAN_Fault",
    "APP_Fault",
    "EngineRPM_Fault",
    "FanClutchOutputShort",
    "FanClutchOutputOpen",
    "FanClutchOutputFault",
  ],
  41806: [
    "InputPowerLow",
    "InputPowerHigh",
    "InputPowerFault",
    "SensorPowerLow",
    "SensorPowerHigh",
    "SensorPowerFault",
    "EngagementFault",
    "DisengagementFault",
    "Input1_Fault",
    "Input2_Fault",
    "Input3_Fault",
    "Input4_Fault",
    "Input5_Fault",
    "Input6_Fault",
    "Input7_Fault",
    "Input8_Fault",
  ],
};

function decodeFaultRegister(rawValue, registerAddress) {
  if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") {
    return { decimal: "-", faults: [] };
  }
  const value = Number(rawValue);
  if (!Number.isInteger(value) || value < 0 || value > 65535) {
    return { decimal: rawValue, faults: [] };
  }
  const faultMap = FAULT_MAPS[registerAddress];
  if (!faultMap) return { decimal: value, faults: [] };

  const binary = value.toString(2).padStart(16, "0");
  const faults = [];
  for (let i = 0; i < 16; i++) {
    if (binary[i] === "1") {
      faults.push({ bit: i + 1, name: faultMap[i] });
    }
  }
  return { decimal: value, faults };
}

const FAN_CLUTCH_REGISTERS = ["41800","41801","41802","41803","41804","41805","41806"];

const FAN_CLUTCH_FIELDS = [
  { reg: "41800", label: "PWM Duty Cycle",        isFault: false },
  { reg: "41801", label: "Input Speed",            isFault: false },
  { reg: "41802", label: "Fan Command",            isFault: false },
  { reg: "41803", label: "Actual Fan Speed",       isFault: false },
  { reg: "41804", label: "Fan Drive Input Index",  isFault: false },
  { reg: "41805", label: "Fault Msg 1",            isFault: true  },
  { reg: "41806", label: "Fault Msg 2",            isFault: true  },
];

const parseRegData = (regData) =>
  FAN_CLUTCH_FIELDS.map((f) => {
    const raw = regData?.[f.reg];
    if (f.isFault) {
      const decoded = decodeFaultRegister(raw, Number(f.reg));
      return { ...f, value: decoded.decimal, faults: decoded.faults };
    }
    if (raw === undefined || raw === null) return { ...f, value: "-", faults: [] };
    return { ...f, value: uint16ToInt16(Number(raw)), faults: [] };
  });

export default function FanClutchC5({ selectedDevice, initialData }) {
  const [loading, setLoading] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSuccess, setProgressSuccess] = useState(false);
  const [progressError, setProgressError] = useState("");
  const pollRef = useRef(null);
  const [fields, setFields]   = useState(() => parseRegData(initialData?.response));

  const unitId = selectedDevice?.id;

  const fetchData = async () => {
    try {
      setLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        FAN_CLUTCH_REGISTERS,
      );
      setFields(parseRegData(resp.data.data?.response));
    } catch (err) {
      console.error("Fan clutch fetch error:", err);
      toast.error("Failed to load fan clutch data");
    } finally {
      setLoading(false);
    }
  };

  const handlePoll = async () => {
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 1: get initial time
    const initialResp = await axios.get(
      `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${unitId}`
    );
    let lastTime = Number(initialResp.data.data?.time || 0);
    // Step 2: send G command
    console.log("🟢 Sending G command...");
    await axios.post(
      `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
      { unitId: unitId, cmd: "G",UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: G`,},
      {
        headers: {
          id: "anything",
          "Content-Type": "application/json",
        },
      }
    );
    await new Promise((resolve) => setTimeout(resolve, 10000));
    // Step 3: start polling every second
    let counter = 0;
    pollRef.current = setInterval(async () => {
      try {
        counter++;
        console.log(`⏳ Checking device (${counter}s)...`);
        const secondResp = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${unitId}`
        );
        const currentTime = Number(secondResp.data.data?.time || 0);
        console.log("currentTime", currentTime);
        if (currentTime && currentTime !== lastTime) {
          console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          clearInterval(pollRef.current);
          pollRef.current = null;
          setTimeout(() => {
            setProgressSuccess(true);
          }, 5000);
        }

        if (counter > 40) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          console.warn("⏰ Timeout waiting for device update.");
          setProgressError(
            "⏰ Timeout waiting for device response. Please try again."
          );
        }
      } catch (err) {
        console.warn("Polling error:", err.message);
      }
    }, 4000); // poll every second
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100vw" }}>
      {/* Header */}
      <Box sx={{ bgcolor: "#1e293b", px: 2, py: 1, display: "flex", alignItems: "center" }}>
        <Typography sx={{ color: "#fff", fontSize: 14 }}>Fan Clutch</Typography>
        <Box sx={{ flexGrow: 1, display: "flex", gap:"10px", justifyContent: "flex-end" }}>
          <Button 
          disabled={loading}
          sx={{
            backgroundColor: "#00BFFF",
            color: "white",
            borderRadius: "8px",
            fontWeight: 500,
            padding: "6px 12px",
            fontSize: {
              xs: "0.4rem",
              sm: "0.6rem",
              md: "0.875rem",
            },
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            transition: "all 0.2s",
            "&:hover:not(:disabled)": {
              backgroundColor: "#18a5d4ff",
            },
            "&:disabled": {
              opacity: 0.7,
              cursor: "not-allowed",
            },
          }} onClick={fetchData}>
            <Refresh
                        sx={{
                          width: "16px",
                          height: "16px",
                          animation: loading ? "spin 1s linear infinite" : "none",
                          "@keyframes spin": {
                            "0%": { transform: "rotate(0deg)" },
                            "100%": { transform: "rotate(360deg)" },
                          },
                        }}
                      />
            Refresh
          </Button>
          <Button disabled={progressOpen}
            sx={{
              backgroundColor: "#00BFFF",
              color: "white",
              borderRadius: "8px",
              fontWeight: 500,
              padding: "6px 12px",
              fontSize: {
                xs: "0.4rem",
                sm: "0.6rem",
                md: "0.875rem",
              },
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              transition: "all 0.2s",
              "&:hover:not(:disabled)": {
                backgroundColor: "#18a5d4ff",
              },
              "&:disabled": {
                opacity: 0.7,
                cursor: "not-allowed",
              },
            }} onClick={handlePoll}>
            <Download sx={{ width: "16px", height: "16px" }} />
            Poll
          </Button>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Box sx={{ bgcolor: "#1e293b", borderRadius: 2, p: 2 }}>
            <Typography sx={{ color: "white", mb: 1, fontWeight: "bold" }}>
              Fan Clutch Data
            </Typography>

            {fields.map((f, idx) => (
              <Box
                key={f.reg}
                sx={{
                  py: 0.75,
                  borderBottom: idx < fields.length - 1 ? "1px solid #334155" : "none",
                }}
              >
                {f.isFault ? (
                  // ── Fault row ─────────────────────────────────────────────
                  <>
                    {/* First line: label on left, value pinned on right */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography sx={{ color: "#cbd5e5", fontSize: 13, flexShrink: 0 }}>
                        {f.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: 13,
                          flexShrink: 0,
                          color: f.faults.length > 0 ? "#f15757" : "#4ade80",
                        }}
                      >
                        {f.value}
                      </Typography>
                    </Box>

                    {/* Second line: chips wrap freely underneath */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 0.5, mt: 0.5 }}>
                      {f.faults.length === 0 ? (
                        <Chip
                          label="No faults"
                          size="small"
                          sx={{
                            bgcolor: "#14532d",
                            color: "#4ade80",
                            fontSize: 10,
                            height: 20,
                            borderRadius: "4px",
                          }}
                        />
                      ) : (
                        f.faults.map((fault) => (
                          <Chip
                            key={fault.bit}
                            label={fault.name}
                            size="small"
                            sx={{
                              bgcolor: "#7f1d1d",
                              color: "#fca5a5",
                              fontSize: 10,
                              height: 20,
                              borderRadius: "4px",
                            }}
                          />
                        ))
                      )}
                    </Box>
                  </>
                ) : (
                  // ── Normal row ────────────────────────────────────────────
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ color: "#cbd5e5", fontSize: 13, flexShrink: 0 }}>
                      {f.label}
                    </Typography>
                    <Typography sx={{ color: "#38bdf8", fontWeight: "bold", fontSize: 13 }}>
                      {f.value}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {/* Progress Dialog */}
            <ProgressDialog
              open={progressOpen}
              isSuccess={progressSuccess}
              errorMessage={progressError}
              onClose={() => {
                setProgressOpen(false);
                setProgressSuccess(false);
                setProgressError("");
              }}
              onRefresh={async () => {
                setProgressOpen(false);
                setProgressSuccess(false);
                setProgressError("");
                await fetchData();
              }}
              loadingMessage="Check Device Response. Operation in-progress ..."
              successMessage="Successfully updated!"
            />
    </Box>
  );
}